export interface LabelBox { left: number; right: number; top: number; bottom: number }

export interface ParkingLabelCandidate {
  id: string;
  capacity: number;
  text: string;
  position: [number, number];
}

/** Greedy screen-space placement; capacities break ties before stable IDs.
 * The conservative monospace bounds include the background and breathing room.
 */
export function chooseParkingLabels<T extends ParkingLabelCandidate>(
  candidates: T[], size: { width: number; height: number }, obstacles: LabelBox[] = [], limit = Infinity,
): T[] {
  const placed: T[] = [];
  const boxes: LabelBox[] = [...obstacles];
  for (const candidate of [...candidates].sort((a, b) => b.capacity - a.capacity || a.id.localeCompare(b.id))) {
    const [x, y] = candidate.position;
    if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x > size.width || y > size.height) continue;
    const halfWidth = (candidate.text.length * 7.2 + 16) / 2;
    const box = { left: x - halfWidth, right: x + halfWidth, top: y - 12, bottom: y + 12 };
    if (box.left < 2 || box.right > size.width - 2 || box.top < 2 || box.bottom > size.height - 2) continue;
    if (boxes.some(b => box.left < b.right && box.right > b.left && box.top < b.bottom && box.bottom > b.top)) continue;
    placed.push(candidate);
    boxes.push(box);
    if (placed.length >= limit) break;
  }
  return placed;
}


/** Use actual control bounds so CSS breakpoints cannot drift from label placement. */
export function mapLabelObstacles(host: HTMLElement | null): LabelBox[] {
  if (!host) return [];
  const origin = host.getBoundingClientRect();
  const controls = [...host.querySelectorAll<HTMLElement>(".building-search, .traffic-legend, .zoom-controls, .map-compass, .map-legend-toggle, .map-attribution, .building-card, .incident-card"), ...document.querySelectorAll<HTMLElement>(".chatbox-closed, .chat-panel")];
  return Array.from(controls).filter(element => element.getClientRects().length > 0).map(element => {
    const box = element.getBoundingClientRect();
    return { left: box.left - origin.left - 6, right: box.right - origin.left + 6,
      top: box.top - origin.top - 6, bottom: box.bottom - origin.top + 6 };
  });
}
